import React, { useMemo, useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import '../App.css';
import '../mobile.css';
import '../index.css';
import '../components/Onboarding/style.css';
import {
  PROTEIN_OPTIONS as PROTEINS,
  CARB_OPTIONS as CARBS,
  FAT_OPTIONS as FATS,
} from '../constants/nutritionOptions';
import bodyTypeOutcomes from '../constants/body_type_outcomes.json';
import { CancelIcon, InfoIcon } from '../assets/icons/icons';
import men4to9Img from '../assets/bodyfat/men-4-9.png';
import men10to15Img from '../assets/bodyfat/men-10-15.png';
import men16to20Img from '../assets/bodyfat/men-16-20.png';
import men21to25Img from '../assets/bodyfat/men-21-25.png';
import men26to30Img from '../assets/bodyfat/men-26-30.png';
import men31to40Img from '../assets/bodyfat/men-31-40.png';
import women10to14Img from '../assets/bodyfat/women-10-14.png';
import women15to20Img from '../assets/bodyfat/women-15-20.png';
import women21to25Img from '../assets/bodyfat/women-21-25.png';
import women26to30Img from '../assets/bodyfat/women-26-30.png';
import women31to35Img from '../assets/bodyfat/women-31-35.png';
import women36to40Img from '../assets/bodyfat/women-36-40.png';

// Utility options
const GOALS = ['Weight Loss', 'Weight Gain', 'Muscle Size', 'Fatloss', 'Health/Longevitiy'];
const GOAL_DETAILS = {
  'Weight Loss': { title: 'Weight Loss', desc: 'Focus on losing fat and improving definition' },
  'Weight Gain': { title: 'Weight Gain', desc: 'Focus on gaining body weight and strength' },
  'Muscle Size': { title: 'Muscle Size', desc: 'Focus on building muscle size and strength' },
  // Store value 'Fatloss' but display label 'Fat Loss'
  'Fatloss': { title: 'Fat Loss', desc: 'Focus on losing fat while maintaining muscle' },
  'Health/Longevitiy': { title: 'Health/Longevitiy', desc: 'Maintain and improve overall health and fitness' },
};

const BODY_FAT_RANGES = [
  {
    key: 'range-4-14',
    men: {
      label: 'Men 4-9%',
      visuals: [{ src: men4to9Img, label: 'Men 4-9% body fat' }],
    },
    women: {
      label: 'Women 10-14%',
      visuals: [{ src: women10to14Img, label: 'Women 10-14% body fat' }],
    },
  },
  {
    key: 'range-10-20',
    men: {
      label: 'Men 10-15%',
      visuals: [{ src: men10to15Img, label: 'Men 10-15% body fat' }],
    },
    women: {
      label: 'Women 15-20%',
      visuals: [{ src: women15to20Img, label: 'Women 15-20% body fat' }],
    },
  },
  {
    key: 'range-16-25',
    men: {
      label: 'Men 16-20%',
      visuals: [{ src: men16to20Img, label: 'Men 16-20% body fat' }],
    },
    women: {
      label: 'Women 21-25%',
      visuals: [{ src: women21to25Img, label: 'Women 21-25% body fat' }],
    },
  },
  {
    key: 'range-21-35',
    men: {
      label: 'Men 21-30%',
      visuals: [
        { src: men21to25Img, label: 'Men 21-25% body fat' },
        { src: men26to30Img, label: 'Men 26-30% body fat' },
      ],
    },
    women: {
      label: 'Women 26-35%',
      visuals: [
        { src: women26to30Img, label: 'Women 26-30% body fat' },
        { src: women31to35Img, label: 'Women 31-35% body fat' },
      ],
    },
  },
  {
    key: 'range-31-40',
    men: {
      label: 'Men 31-40%',
      visuals: [{ src: men31to40Img, label: 'Men 31-40% body fat' }],
    },
    women: {
      label: 'Women 36-40%',
      visuals: [{ src: women36to40Img, label: 'Women 36-40% body fat' }],
    },
  },
  {
    key: 'range-41-plus',
    note: 'Using our highest reference range (31-40%) for 41%+ until a dedicated visual is available.',
    men: {
      label: 'Men 41%+',
      visuals: [{ src: men31to40Img, label: 'Men 41%+ body fat reference' }],
    },
    women: {
      label: 'Women 41%+',
      visuals: [{ src: women36to40Img, label: 'Women 41%+ body fat reference' }],
    },
  },
];

const BODY_TYPE_INFO = {
  'Ectomorph': {
    subtitle: 'Slim/lean frame, fast metabolism, gains come slowly.',
    characteristics: [
      'Slim and lean physique with narrower shoulders and low body fat.',
      'Fast metabolism; struggles to gain weight or muscle.',
    ],
    examples: [
      'College student who rarely works out, naturally thin, usually wears size small/medium.',
    ],
  },
  'Mesomorph': {
    subtitle: 'Naturally muscular, broad shoulders, gains muscle easily.',
    characteristics: [
      'Naturally muscular build with broad shoulders and a narrow waist.',
      'Gains muscle easily and can maintain weight with less effort.',
    ],
    examples: [
      'Middle-aged person with a strong physique, attends occasional fitness classes, stays toned without strict training.',
    ],
  },
  'Endomorph': {
    subtitle: 'Stockier build, gains weight easily, can build muscle.',
    characteristics: [
      'Stocky structure with wider hips and tendency to gain weight.',
      'Can carry higher body fat while still building muscle.',
    ],
    examples: [
      'Parent working a desk job, enjoys family meals, finds leanness challenging and seeks ways to manage weight.',
    ],
  },
  'Ecto-Mesomorph': {
    subtitle: 'Lean but muscular with balanced physique.',
    characteristics: [
      'Slim yet muscular with good definition and balance.',
      'Can build muscle and strength relatively easily.',
    ],
    examples: [
      'Young professional who attends fitness classes regularly, maintains a lean and toned build without competing.',
    ],
  },
  'Meso-Endomorph': {
    subtitle: 'Muscular but stores fat, strong frame.',
    characteristics: [
      'Muscular build that tends to store fat; broad shoulders with rounder waist.',
      'Gains muscle easily but may struggle with fat loss due to metabolism.',
    ],
    examples: [
      'Lifts weights and is somewhat active but finds body fat hard to keep low, often needs larger sizes due to muscle and fat.',
    ],
  },
  'Ecto-Endomorph': {
    subtitle: 'Long limbs, carries more midsection fat.',
    characteristics: [
      'Longer limbs with a tendency to carry more body fat, especially midsection.',
      'May find it harder to gain muscle efficiently while managing body fat.',
    ],
    examples: [
      'Occasional gym-goer with extra abdominal weight; prefers yoga or Pilates to manage physique.',
    ],
  },
};

const PROF_ACTIVITY = ['Sedentary', 'Moderately Active', 'Active', 'Very Active'];

const BODY_TYPE_ASSESSMENT_TITLE = "Now let's determine your body type";
const BODY_TYPE_QUESTIONS = [
  {
    key: 'goals_bodytype_composition',
    field: 'bodyComposition',
    question: 'How would you describe your body composition?',
    options: [
      'Lean with some fat',
      'Muscular',
      'Higher fat with some muscle',
      'Slim but soft (skinny fat)',
      'Obese',
    ],
  },
  {
    key: 'goals_bodytype_weight',
    field: 'weightManagement',
    question: 'How easy is it for you to manage your weight?',
    options: [
      'Very easy to gain',
      'Somewhat easy to gain',
      'Neutral',
      'Somewhat difficult to gain',
      'Very difficult to gain',
    ],
  },
  {
    key: 'goals_bodytype_diet',
    field: 'dietaryReaction',
    question: 'How does your body respond to dietary changes?',
    options: [
      'Quick weight changes',
      'Slow weight changes',
      'No noticeable effect (or have not tried)',
      'Weight fluctuates',
    ],
  },
  {
    key: 'goals_bodytype_energy',
    field: 'energyLevels',
    question: 'Describe your energy levels throughout the day:',
    options: [
      'Consistently high',
      'Fluctuating',
      'Consistently low',
      'Varies with diet/exercise',
    ],
  },
];

const BODY_TYPE_OUTCOME_MAP = new Map(
  (bodyTypeOutcomes?.body_type_outcomes || []).map((entry) => {
    const key = [
      entry.body_composition,
      entry.weight_management,
      entry.dietary_reaction,
      entry.energy_levels,
    ].join('||');
    return [key, entry.body_type];
  })
);

const determineBodyTypeFromAnswers = ({ bodyComposition, weightManagement, dietaryReaction, energyLevels }) => {
  if (!bodyComposition || !weightManagement || !dietaryReaction || !energyLevels) return '';
  const lookupKey = [bodyComposition, weightManagement, dietaryReaction, energyLevels].join('||');
  return BODY_TYPE_OUTCOME_MAP.get(lookupKey) || '';
};

const ACTIVITY_INFO = {
  'Sedentary': {
    subtitle: 'Mostly seated with minimal daily movement.',
    characteristics: [
      'Little to no physical activity during the day.',
      'Long periods of sitting (desk work, computer, TV, or gaming).',
      'Movement limited to short activities like basic tasks (e.g., light household chores).',
    ],
    examples: [
      'Office worker who sits at a desk most of the day with few movement breaks.',
      'Minimal engagement in physical activities outside of work, preferring low-exertion indoor activities (e.g., reading, watching movies).',
    ],
  },
  'Moderately Active': {
    subtitle: 'Light exercise 1-3 times/week along with regular movement throughout the day.',
    characteristics: [
      'Some physical activity or light exercise 1-3 times a week.',
      'Integrates light movement into daily life (e.g., walking, gardening).',
      'Moderate activity outside of the workout routine, such as walking or commuting.',
    ],
    examples: [
      'Takes brisk walks a few times a week or participates in casual fitness classes or sports.',
      'Engages in short bouts of movement during the day, like lunchtime walks or light home workouts, alongside a mostly sedentary job.',
    ],
  },
  'Active': {
    subtitle: 'Regular workouts of 3-5 times/week with a mix of cardio and strength.',
    characteristics: [
      'Consistent exercise and physical activity 3-5 times per week.',
      'Includes a balance of cardio and strength training, along with daily activities that promote energy expenditure.',
      'Engages in non-workout physical activities (e.g., walking or biking short distances).',
    ],
    examples: [
      'Jogs several times per week, attends gym workouts focused on strength training, or participates in group fitness classes.',
      'Incorporates walking or other physical activities into daily routines alongside regular workout sessions.',
    ],
  },
  'Very Active': {
    subtitle: 'Intense training 5+ times/week, including structured workout and high movement lifestyle.',
    characteristics: [
      'High levels of physical activity exceeding 5 times per week.',
      'Engages in structured workouts, sports, and has an active lifestyle, resulting in significant daily movement.',
      'Often trains for specific goals, competitions, or events.',
    ],
    examples: [
      'An athlete training intensely for hours each week, competing in various sports, or following a rigorous fitness regimen (such as CrossFit or endurance training).',
      'Actively participates in various activities outside of structured workouts, such as hiking or recreational sports, enhancing daily energy expenditure.',
    ],
  },
};

const DESCRIBE = [
  'Sedentary Adult',
  'Exercising Adult',
  'Competive Athlete',
  'Growing Teenage Athlete',
  'Adult Building Muscle',
  'Athlete Restricting Calories',
];

const initialState = {
  // Section 1
  name: '',
  age: '',
  gender: '',
  heightFeet: '',
  heightInches: '',
  weightLbs: '',
  assessmentImages: [],
  // Section 2
  primaryGoal: '',
  bodyFat: '',
  bodyType: '',
  bodyComposition: '',
  weightManagement: '',
  dietaryReaction: '',
  energyLevels: '',
  professionActivity: '',
  describe: '',
  weeklyActivities: '',
  stepsPerDay: '',
  // Section 3
  proteins: [],
  carbs: [],
  fats: [],
};

const MAX_ASSESSMENT_IMAGES = 5;
const MAX_ASSESSMENT_IMAGE_MB = 5;
const MAX_ASSESSMENT_IMAGE_BYTES = MAX_ASSESSMENT_IMAGE_MB * 1024 * 1024;
const IMAGE_BUCKET = process.env.REACT_APP_SUPABASE_IMAGE_BUCKET || 'coach-images';

const getFileExtension = (file) => {
  const name = file?.name || '';
  const nameExt = name.includes('.') ? name.split('.').pop() : '';
  const typeExt = file?.type?.split('/')?.[1] || '';
  const raw = nameExt || typeExt || 'jpg';
  const cleaned = raw.toLowerCase().replace(/[^a-z0-9]/g, '');
  return cleaned || 'jpg';
};

const buildImagePath = (userId, folder, file, index) => {
  const ext = getFileExtension(file);
  const unique = `${Date.now()}-${index}-${Math.random().toString(16).slice(2, 8)}`;
  return `${userId}/${folder}/${unique}.${ext}`;
};

export default function OnboardingPage() {
  const [form, setForm] = useState(initialState);
  // We break the flow into many pages (sub-steps)
  const pages = useMemo(() => {
    const bodyTypeSteps = BODY_TYPE_QUESTIONS.map((step) => ({
      key: step.key,
      section: 'Goals',
      title: BODY_TYPE_ASSESSMENT_TITLE,
      validate: () => !!form[step.field],
    }));
    return [
      // Basics
      { key: 'basics_name', section: 'Basics', title: 'Your name', validate: () => form.name.trim().length > 1 },
      {
        key: 'basics_age_gender', section: 'Basics', title: 'Age and gender', validate: () => {
          const age = Number(form.age); return !!form.gender && Number.isFinite(age) && age > 0;
        }
      },
      {
        key: 'basics_height', section: 'Basics', title: 'Your height', validate: () => {
          const feetRaw = `${form.heightFeet ?? ''}`.trim();
          const inchesRaw = `${form.heightInches ?? ''}`.trim();
          const hf = Number(feetRaw);
          const hi = Number(inchesRaw);
          const totalPositive = (hf > 0 || hi > 0);
          return feetRaw !== '' && inchesRaw !== '' && Number.isFinite(hf) && hf >= 0 && hf < 9 && Number.isFinite(hi) && hi >= 0 && hi < 12 && totalPositive;
        }
      },
      { key: 'basics_weight', section: 'Basics', title: 'Your weight', validate: () => { const w = Number(form.weightLbs); return Number.isFinite(w) && w > 0; } },
      // Goals
      { key: 'goals_primary', section: 'Goals', title: 'Primary goal', validate: () => !!form.primaryGoal },
      { key: 'goals_bodyfat', section: 'Goals', title: 'Body fat estimate', validate: () => !!form.bodyFat },
      ...bodyTypeSteps,
      { key: 'goals_activity', section: 'Goals', title: 'Daytime activity level', validate: () => !!form.professionActivity },
      { key: 'goals_describe', section: 'Goals', title: 'What best describes you?', validate: () => !!form.describe },
      {
        key: 'goals_steps',
        section: 'Goals',
        title: 'Average steps per day',
        validate: () => {
          const raw = `${form.stepsPerDay ?? ''}`.trim();
          const s = Number(raw);
          return raw !== '' && Number.isFinite(s) && s >= 0 && s <= 100000;
        }
      },
      { key: 'goals_weekly', section: 'Goals', title: 'Your weekly activities', validate: () => !!(`${form.weeklyActivities ?? ''}`.trim()) },
      { key: 'goals_photos', section: 'Goals', title: 'Progress photos (optional)', validate: () => true },
      // Nutrition (split into 3 screens)
      { key: 'nutr_proteins', section: 'Nutrition', title: 'Proteins you will eat', validate: () => form.proteins.length >= 4 },
      { key: 'nutr_carbs', section: 'Nutrition', title: 'Carbs you will eat', validate: () => form.carbs.length >= 4 },
      { key: 'nutr_fats', section: 'Nutrition', title: 'Fat sources you will eat', validate: () => form.fats.length >= 4 },
    ];
  }, [form]);

  const [pageIndex, setPageIndex] = useState(0);
  const totalPages = pages.length;
  const progress = useMemo(() => Math.round(((pageIndex + 1) / totalPages) * 100), [pageIndex, totalPages]);
  const sections = ['Basics', 'Goals', 'Nutrition'];
  const sectionIndex = sections.indexOf(pages[pageIndex].section);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showActivityInfo, setShowActivityInfo] = useState(false);
  const [bodyFatReferenceRange, setBodyFatReferenceRange] = useState('');
  const [imageError, setImageError] = useState('');

  // Load draft from localStorage
  useEffect(() => {
    const draft = localStorage.getItem('onboarding_profile_draft');
    if (draft) {
      try { setForm({ ...initialState, ...JSON.parse(draft), assessmentImages: [] }); } catch { }
    }
  }, []);

  useEffect(() => {
    const draft = { ...form, assessmentImages: [] };
    localStorage.setItem('onboarding_profile_draft', JSON.stringify(draft));
  }, [form]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const removeAssessmentImage = async (index) => {
    const target = form.assessmentImages?.[index];
    if (target?.previewUrl && target.previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(target.previewUrl);
    }
    if (target?.path) {
      try {
        await supabase.storage.from(IMAGE_BUCKET).remove([target.path]);
      } catch {
        // ignore delete issues
      }
    }
    setForm((prev) => {
      const next = [...(prev.assessmentImages || [])];
      next.splice(index, 1);
      return { ...prev, assessmentImages: next };
    });
  };

  const handleAssessmentImages = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    setImageError('');

    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData?.session?.user?.id;
    if (!userId) {
      setImageError('Sign in to upload images.');
      return;
    }

    const current = form.assessmentImages || [];
    const remaining = MAX_ASSESSMENT_IMAGES - current.length;
    if (remaining <= 0) {
      setImageError(`You can upload up to ${MAX_ASSESSMENT_IMAGES} images.`);
      return;
    }

    const next = [...current];
    const slice = files.slice(0, remaining);
    for (const [idx, file] of slice.entries()) {
      if (!file.type.startsWith('image/')) {
        setImageError('Only image files are allowed.');
        continue;
      }
      if (file.size > MAX_ASSESSMENT_IMAGE_BYTES) {
        setImageError(`Images must be ${MAX_ASSESSMENT_IMAGE_MB}MB or smaller.`);
        continue;
      }
      let previewUrl = '';
      try {
        const path = buildImagePath(userId, 'assessment', file, current.length + idx + 1);
        previewUrl = URL.createObjectURL(file);
        const { error: uploadError } = await supabase.storage
          .from(IMAGE_BUCKET)
          .upload(path, file, { upsert: false });
        if (uploadError) throw uploadError;
        next.push({ path, previewUrl, name: file.name });
      } catch {
        if (previewUrl && previewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(previewUrl);
        }
        setImageError('Unable to upload one of the images.');
      }
    }
    if (files.length > remaining) {
      setImageError(`You can upload up to ${MAX_ASSESSMENT_IMAGES} images.`);
    }
    update({ assessmentImages: next });
  };

  const toggleListValue = (key, value) => {
    setForm((f) => {
      const list = new Set(f[key]);
      list.has(value) ? list.delete(value) : list.add(value);
      return { ...f, [key]: Array.from(list) };
    });
  };

  const selectAll = (key, items) => update({ [key]: items.slice() });
  const clearAll = (key) => update({ [key]: [] });

  const derivedBodyType = useMemo(() => determineBodyTypeFromAnswers({
    bodyComposition: form.bodyComposition,
    weightManagement: form.weightManagement,
    dietaryReaction: form.dietaryReaction,
    energyLevels: form.energyLevels,
  }), [form.bodyComposition, form.weightManagement, form.dietaryReaction, form.energyLevels]);
  const resolvedBodyType = derivedBodyType || form.bodyType || '';

  useEffect(() => {
    setForm((prev) => {
      const nextBodyType = derivedBodyType || '';
      if (prev.bodyType === nextBodyType) return prev;
      if (nextBodyType) {
      }
      return { ...prev, bodyType: nextBodyType };
    });
  }, [derivedBodyType]);

  // Validation per step
  const canNext = pages[pageIndex]?.validate?.() ?? true;

  const submitProfile = async () => {
    setSaving(true);
    setError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id || null;

      const imagePaths = (form.assessmentImages || [])
        .map((img) => (typeof img === 'string' ? img : img?.path))
        .filter(Boolean);

      const payload = {
        user_id: userId,
        basics: {
          name: form.name.trim(),
          age: Number(form.age),
          gender: form.gender,
          height: { feet: Number(form.heightFeet), inches: Number(form.heightInches) },
          weight_lbs: Number(form.weightLbs),
        },
        goals: {
          primary: form.primaryGoal,
          body_fat: form.bodyFat,
          body_type: resolvedBodyType,
          profession_activity: form.professionActivity,
          describe: form.describe,
          weekly_activities: form.weeklyActivities || '',
          steps_per_day: Number(form.stepsPerDay),
        },
        nutrition: {
          proteins: form.proteins,
          carbs: form.carbs,
          fats: form.fats,
        },
        media: {
          images: imagePaths,
        },
        created_at: new Date().toISOString(),
      };

      // Try Supabase upsert (table: profiles). Store JSON columns if available.
      if (userId) {
        // Upsert core profile with explicit basics columns for easy access
        try {
          const profileRow = {
            user_id: userId,
            assessment: payload,
            completed: true,
            name: payload.basics.name,
            age: payload.basics.age,
            gender: payload.basics.gender,
            height_feet: payload.basics.height.feet,
            height_inches: payload.basics.height.inches,
            weight_lbs: payload.basics.weight_lbs,
            reviewed_at: null,
            reviewed_by: null,
          };
          const { error: dbErr } = await supabase
            .from('assessments')
            .upsert(profileRow, { onConflict: 'user_id' });
          if (dbErr) throw dbErr;
        } catch (e) {
          // Swallow error so local save still succeeds
          console.warn('profiles upsert failed', e?.message);
        }

        // Upsert nutrition preferences into a separate table
        try {
          const { error: nutErr } = await supabase
            .from('nutrition_preferences')
            .upsert({
              user_id: userId,
              proteins: form.proteins,
              carbs: form.carbs,
              fats: form.fats,
            }, { onConflict: 'user_id' });
          if (nutErr) throw nutErr;
        } catch (e) {
          console.warn('nutrition_preferences upsert failed', e?.message);
        }
      }

      const storagePayload = {
        ...payload,
        media: { ...(payload.media || {}), images: [] },
      };
      localStorage.setItem('onboarding_profile', JSON.stringify(storagePayload));
      localStorage.removeItem('onboarding_profile_draft');
      // Flag to trigger welcome modal on next page
      localStorage.setItem('just_onboarded', '1');
      window.location.href = '/';
    } catch (e) {
      setError('Saved locally. Server save failed: ' + (e?.message || 'unknown error'));
      // Ensure local save at least
      const fallback = { fallback: true, ...form, assessmentImages: [] };
      localStorage.setItem('onboarding_profile', JSON.stringify(fallback));
    } finally {
      setSaving(false);
    }
  };

  const next = () => setPageIndex((i) => Math.min(totalPages - 1, i + 1));
  const back = () => setPageIndex((i) => Math.max(0, i - 1));

  // Page rendering based on current page key
  const current = pages[pageIndex];
  const sectionLabel = current?.section || '';
  const title = current?.title || '';

  const isLastPage = pageIndex === totalPages - 1;
  const genderKey = form.gender === 'Female' ? 'women' : form.gender === 'Male' ? 'men' : null;
  const bodyFatOptions = useMemo(() => {
    return BODY_FAT_RANGES.map((range) => {
      if (!genderKey) {
        return { key: range.key, label: `${range.men.label} / ${range.women.label}` };
      }
      const entry = range[genderKey];
      return { key: range.key, label: entry?.label || range.men.label };
    });
  }, [genderKey]);

  useEffect(() => {
    setForm((prev) => {
      if (!prev.bodyFat) return prev;
      const stillValid = bodyFatOptions.some((opt) => opt.label === prev.bodyFat);
      if (stillValid) return prev;
      return { ...prev, bodyFat: '' };
    });
  }, [bodyFatOptions, setForm]);

  const referenceRange = useMemo(
    () => BODY_FAT_RANGES.find((range) => range.key === bodyFatReferenceRange) || null,
    [bodyFatReferenceRange]
  );
  const referenceLabel = useMemo(() => {
    if (!referenceRange) return '';
    if (genderKey) {
      const entry = referenceRange[genderKey];
      if (entry?.label) return entry.label;
    }
    return `${referenceRange?.men?.label ?? ''}${referenceRange?.men && referenceRange?.women ? ' / ' : ''}${referenceRange?.women?.label ?? ''}`;
  }, [referenceRange, genderKey]);
  const referenceVisuals = useMemo(() => {
    if (!referenceRange) return [];
    if (genderKey) {
      const entry = referenceRange[genderKey];
      if (entry?.visuals?.length) return entry.visuals;
    }
    return [
      ...(referenceRange?.men?.visuals || []),
      ...(referenceRange?.women?.visuals || []),
    ];
  }, [referenceRange, genderKey]);
  const referenceNote = useMemo(() => {
    if (!referenceRange) return '';
    if (referenceRange.key === 'range-41-plus') {
      return 'This is our highest visual reference range image. If you are above this, please select this option.';
    }
    const genderNote = genderKey && referenceRange?.[genderKey]?.note;
    return genderNote || referenceRange?.note || '';
  }, [referenceRange, genderKey]);
  const hasBodyFatImages = referenceVisuals.length > 0;
  const bodyFatGridVariant = referenceVisuals.length === 4 ? 'bodyfat-grid-two' : 'bodyfat-grid-row';
  const allBodyTypeQuestionsAnswered = BODY_TYPE_QUESTIONS.every((step) => !!form[step.field]);
  const bodyTypeSummary = resolvedBodyType ? BODY_TYPE_INFO[resolvedBodyType] : null;
  const finalBodyTypeStepKey = BODY_TYPE_QUESTIONS[BODY_TYPE_QUESTIONS.length - 1]?.key;

  const renderBodyTypeQuestion = (step) => {
    const isFinalStep = step.key === finalBodyTypeStepKey;
    return (
      <>
        <div className="onb-card">
          <p className="section-help">{step.question}</p>
          <div className="choice-list">
            {step.options.map((option) => {
              const selected = form[step.field] === option;
              return (
                <div key={option} className={`option-row ${selected ? 'selected' : ''}`} onClick={() => update({ [step.field]: option })}>
                  <div className="option-leading">{selected ? '●' : '○'}</div>
                  <div className="option-content">
                    <div className="option-title">{option}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </>
    );
  };

  const renderPageBody = () => {
    const bodyTypeStep = BODY_TYPE_QUESTIONS.find((step) => step.key === current.key);
    if (bodyTypeStep) return renderBodyTypeQuestion(bodyTypeStep);
    switch (current.key) {
      // Basics
      case 'basics_name':
        return (
          <>
            <p className="section-help">We use this to personalize your plan.</p>
            <div className="field">
              <label className="label">Name</label>
              <input type="text" value={form.name} onChange={(e) => update({ name: e.target.value })} placeholder="Your name" />
            </div>
          </>
        );
      case 'basics_age_gender':
        return (
          <div className="grid-2">
            <div className="field">
              <label className="label">Age</label>
              <input type="number" min={0} value={form.age} onChange={(e) => update({ age: e.target.value })} placeholder="Age" />
            </div>
            <div className="field">
              <label className="label">Gender</label>
              <select value={form.gender} onChange={(e) => update({ gender: e.target.value })}>
                <option value="">Select</option>
                <option>Male</option>
                <option>Female</option>
              </select>
            </div>
          </div>
        );
      case 'basics_height':
        return (
          <div className="field">
            <label className="label">Height</label>
            <div className="grid-2">
              <input type="number" min={0} max={8} value={form.heightFeet} onChange={(e) => update({ heightFeet: e.target.value })} placeholder={"Feet (e.g., 5)"} />
              <input type="number" min={0} max={11} value={form.heightInches} onChange={(e) => update({ heightInches: e.target.value })} placeholder={"Inches (e.g., 10)"} />
            </div>
            <span className="hint">Example: 5 and 10 = 5'10"</span>
          </div>
        );
      case 'basics_weight':
        return (
          <div className="field">
            <label className="label">Weight (lbs)</label>
            <input type="number" min={0} value={form.weightLbs} onChange={(e) => update({ weightLbs: e.target.value })} placeholder="e.g., 150" />
          </div>
        );

      // Goals
      case 'goals_primary':
        return (
          <div className="onb-card choice-list">
            {GOALS.map((g) => {
              const selected = form.primaryGoal === g;
              const d = GOAL_DETAILS[g];
              return (
                <div key={g} className={`option-row ${selected ? 'selected' : ''}`} onClick={() => update({ primaryGoal: g })}>
                  <div className="option-leading">{selected ? '●' : '○'}</div>
                  <div className="option-content">
                    <div className="option-title">{d?.title || g}</div>
                    {d?.desc && <div className="option-sub">{d.desc}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        );
      case 'goals_bodyfat':
        return (
          <div className="onb-card choice-list">
            {bodyFatOptions.map((option) => {
              const selected = form.bodyFat === option.label;
              return (
                <div key={option.key} className={`option-row ${selected ? 'selected' : ''}`} onClick={() => update({ bodyFat: option.label })}>
                  <div className="option-leading">{selected ? '●' : '○'}</div>
                  <div className="option-content">
                    <div className="option-title">{option.label}</div>
                  </div>
                  <button
                    type="button"
                    className="option-info-button"
                    onClick={(e) => { e.stopPropagation(); setBodyFatReferenceRange(option.key); }}
                    aria-label={`View reference images for ${option.label}`}
                  >
                    <InfoIcon className="option-info-button-icon" aria-hidden="true" />
                    <span className="sr-only">View body fat reference for {option.label}</span>
                  </button>
                </div>
              );
            })}
          </div>
        );
      case 'goals_activity':
        return (
          <>
            <div className="onb-info-banner">
              <div>
                <div className="onb-info-banner-title">Activity Level Breakdown</div>
                <div className="onb-info-banner-copy">Use this guide to match the option that best fits your usual daily activity, including both work and non-workout activities.</div>
              </div>
              <button type="button" className="onb-info-button" onClick={() => setShowActivityInfo(true)}>
                <span className="onb-info-icon" aria-hidden="true">i</span>
                <span>View descriptions</span>
              </button>
            </div>
            <div className="onb-card choice-list">
              {PROF_ACTIVITY.map((o) => {
                const selected = form.professionActivity === o;
                const detail = ACTIVITY_INFO[o];
                return (
                  <div key={o} className={`option-row ${selected ? 'selected' : ''}`} onClick={() => update({ professionActivity: o })}>
                    <div className="option-leading">{selected ? '●' : '○'}</div>
                    <div className="option-content">
                      <div className="option-title">{o}</div>
                      {detail?.subtitle && <div className="option-sub">{detail.subtitle}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        );
      case 'goals_describe':
        return (
          <div className="onb-card choice-list">
            {DESCRIBE.map((o) => {
              const selected = form.describe === o;
              return (
                <div key={o} className={`option-row ${selected ? 'selected' : ''}`} onClick={() => update({ describe: o })}>
                  <div className="option-leading">{selected ? '●' : '○'}</div>
                  <div className="option-content"><div className="option-title">{o}</div></div>
                </div>
              );
            })}
          </div>
        );
      case 'goals_steps':
        return (
          <div className="field">
            <label className="label">Average steps per day</label>
            <input type="number" min={0} max={100000} value={form.stepsPerDay} onChange={(e) => update({ stepsPerDay: e.target.value })} placeholder="e.g., 6000" />
          </div>
        );
      case 'goals_weekly':
        return (
          <div className="field">
            <label className="label">Activities (7-day period)</label>
            <textarea value={form.weeklyActivities} onChange={(e) => update({ weeklyActivities: e.target.value })} placeholder="Resistance/cardio/sports you usually do" />
          </div>
        );
      case 'goals_photos':
        return (
          <div className="onb-card">
            <div className="label">Progress photos (optional)</div>
            <p className="section-help">Upload up to {MAX_ASSESSMENT_IMAGES} images for your coach to review.</p>
            <input type="file" accept="image/*" multiple onChange={handleAssessmentImages} />
            <div className="onb-image-meta">
              <span>{form.assessmentImages?.length || 0} of {MAX_ASSESSMENT_IMAGES} used</span>
            </div>
            {(form.assessmentImages || []).length === 0 ? (
              <div className="hint">No images added yet.</div>
            ) : (
              <div className="onb-image-grid">
                {(form.assessmentImages || []).map((img, idx) => (
                  <div key={`assessment-image-${idx}`} className="onb-image-card">
                    <img src={img.previewUrl || img.url || img.path} alt={`Assessment upload ${idx + 1}`} loading="lazy" />
                    <button type="button" className="onb-image-remove" onClick={() => removeAssessmentImage(idx)}>
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
            {imageError && <div className="onb-image-error">{imageError}</div>}
          </div>
        );

      // Nutrition
      case 'nutr_proteins':
        return (
          <div className="onb-card">
            <div className="label">Select proteins</div>
            <div className="actions" style={{ margin: '8px 0' }}>
              <button className="btn btn-secondary" type="button" onClick={() => selectAll('proteins', PROTEINS)}>Select all</button>
              <button className="btn btn-secondary" type="button" onClick={() => clearAll('proteins')}>Deselect all</button>
            </div>
            <div className="scroll-options choice-list">
              {PROTEINS.map((p) => {
                const selected = form.proteins.includes(p);
                return (
                  <div key={p} className={`option-row ${selected ? 'selected' : ''}`} onClick={() => toggleListValue('proteins', p)}>
                    <div className="option-leading">{selected ? '✓' : '○'}</div>
                    <div className="option-content"><div className="option-title">{p}</div></div>
                  </div>
                );
              })}
            </div>
            <div className="hint">Select at least 4 • Selected: {form.proteins.length}</div>
          </div>
        );
      case 'nutr_carbs':
        return (
          <div className="onb-card">
            <div className="label">Select carbs/fruits/veggies</div>
            <div className="actions" style={{ margin: '8px 0' }}>
              <button className="btn btn-secondary" type="button" onClick={() => selectAll('carbs', CARBS)}>Select all</button>
              <button className="btn btn-secondary" type="button" onClick={() => clearAll('carbs')}>Deselect all</button>
            </div>
            <div className="scroll-options choice-list">
              {CARBS.map((c) => {
                const selected = form.carbs.includes(c);
                return (
                  <div key={c} className={`option-row ${selected ? 'selected' : ''}`} onClick={() => toggleListValue('carbs', c)}>
                    <div className="option-leading">{selected ? '✓' : '○'}</div>
                    <div className="option-content"><div className="option-title">{c}</div></div>
                  </div>
                );
              })}
            </div>
            <div className="hint">Select at least 4 • Selected: {form.carbs.length}</div>
          </div>
        );
      case 'nutr_fats':
        return (
          <div className="onb-card">
            <div className="label">Select fats</div>
            <div className="actions" style={{ margin: '8px 0' }}>
              <button className="btn btn-secondary" type="button" onClick={() => selectAll('fats', FATS)}>Select all</button>
              <button className="btn btn-secondary" type="button" onClick={() => clearAll('fats')}>Deselect all</button>
            </div>
            <div className="scroll-options choice-list">
              {FATS.map((f) => {
                const selected = form.fats.includes(f);
                return (
                  <div key={f} className={`option-row ${selected ? 'selected' : ''}`} onClick={() => toggleListValue('fats', f)}>
                    <div className="option-leading">{selected ? '✓' : '○'}</div>
                    <div className="option-content"><div className="option-title">{f}</div></div>
                  </div>
                );
              })}
            </div>
            <div className="hint">Select at least 4 • Selected: {form.fats.length}</div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="onboarding-container">
      <div className="onboarding-top">
        <div className="sections-progress">
          <div className="sections-label">{sectionLabel}</div>
          <div className="sections-track" aria-label="progress">
            <div className="sections-fill" style={{ width: `${progress}%` }} />
            <div className="sections-dots">
              {sections.map((_, i) => (
                <span key={i} className={`step-dot ${i === sectionIndex ? 'active' : i < sectionIndex ? 'completed' : ''}`}></span>
              ))}
            </div>
          </div>
          <div className="sections-percent">{progress}%</div>
        </div>
      </div>

      <section className="onb-section" style={{ marginTop: 12 }}>
        <div className="page-header">
          <div className="step-subtitle">Getting Started</div>
          <h2 className="page-title">{title}</h2>
          {current.key === 'basics_age_gender' && form.name && (
            <p className="page-subtitle">Nice to meet you, <span className="brand-accent">{form.name}</span>.</p>
          )}
          {current.key === 'goals_primary' && (
            <p className="page-subtitle">This helps tailor recommendations to your goal. You can change it later.</p>
          )}
        </div>
        {renderPageBody()}

        {error && <p style={{ color: '#b91c1c', marginTop: 12 }}>{error}</p>}

        <div className="bottom-actions onb-actions">
          <div className="action-row">
            <button className={`cta-btn--ghost ${!canNext ? 'inactive' : ''}`} onClick={back} disabled={pageIndex === 0}>Back</button>
            {!isLastPage ? (
              <button className="cta-btn" disabled={!canNext} onClick={next}>Next</button>
            ) : (
              <button className="cta-btn" disabled={!canNext || saving} onClick={submitProfile}>{saving ? 'Saving…' : "Let's do it!"}</button>
            )}
          </div>
        </div>
      </section>

      {showActivityInfo && (
        <div className="onb-info-overlay" onClick={() => setShowActivityInfo(false)}>
          <div className="onb-info-modal" role="dialog" aria-modal="true" aria-label="Daytime activity level descriptions" onClick={(e) => e.stopPropagation()}>
            <div className="onb-info-modal-header">
              <div>
                <h3>Activity Level Breakdown</h3>
                <p>Use this guide to match the option that best fits your usual daily activity, including both work and non-workout activities.</p>
              </div>
              <button className="onb-info-close" onClick={() => setShowActivityInfo(false)} aria-label="Close activity info">
                <CancelIcon className="onb-info-close-icon" />
              </button>
            </div>
            <div className="onb-info-listing">
              {PROF_ACTIVITY.map((level) => {
                const detail = ACTIVITY_INFO[level];
                if (!detail) return null;
                return (
                  <div key={level} className="onb-info-level">
                    <div className="onb-info-level-head">
                      <div className="onb-info-level-title">{level}</div>
                      {detail.subtitle && <div className="onb-info-level-sub">{detail.subtitle}</div>}
                    </div>
                    <div className="onb-info-columns">
                      <div>
                        <div className="onb-info-subheading">Lifestyle Characteristics</div>
                        <ul>
                          {detail.characteristics.map((item, idx) => <li key={idx}>{item}</li>)}
                        </ul>
                      </div>
                      <div>
                        <div className="onb-info-subheading">Examples</div>
                        <ul>
                          {detail.examples.map((item, idx) => <li key={idx}>{item}</li>)}
                        </ul>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {bodyFatReferenceRange && (
        <div className="onb-info-overlay" onClick={() => setBodyFatReferenceRange('')}>
          <div
            className="onb-info-modal bodyfat-modal"
            role="dialog"
            aria-modal="true"
            aria-label={`Body fat reference for ${referenceLabel || 'selected range'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="onb-info-modal-header">
              <div>
                <h3>Body fat reference</h3>
                <p>{referenceLabel || 'Select a range to view the visuals.'}</p>
              </div>
              <button className="onb-info-close" onClick={() => setBodyFatReferenceRange('')} aria-label="Close body fat reference">
                <CancelIcon className="onb-info-close-icon" />
              </button>
            </div>
            {hasBodyFatImages ? (
              <div className="bodyfat-visuals">
                {referenceNote && <p className="bodyfat-note">{referenceNote}</p>}
                <div className={`bodyfat-visual-grid ${bodyFatGridVariant}`}>
                  {referenceVisuals.map((img, idx) => (
                    <figure key={`bf-${idx}`} className="bodyfat-visual">
                      <img src={img.src} alt={img.label} loading="lazy" />
                      <figcaption>{img.label}</figcaption>
                    </figure>
                  ))}
                </div>
              </div>
            ) : (
              <p className="bodyfat-note">Visual reference coming soon for this range.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
